/**
 * In-browser API mock for the `bulk-royalty-statements` prototype.
 *
 * Registers its routes with the shared dev mode (src/lib/dev-mode). When the
 * auth bypass is active (no real Stytch keys / no Django backend on :8000),
 * dev-mode's fetch patch serves this in-memory (localStorage-backed) data, so
 * the prototype is usable with no backend.
 *
 * Scope: only what the royalty-statement flow touches — this prototype is about
 * generating royalty statements for a whole catalogue in one run. Everything
 * else in the app is deliberately left empty.
 *   - Royalty run: default period, preview (contracts derived from the period),
 *     start (background job), batch progress / retry / undo / archive.
 *   - Royalty statements: list (search, filters, sort, batch scope), detail,
 *     html, mark sent / paid, delete, regenerate.
 *   - Contracts, payees, vendors, label: enough to back the above.
 * Any other API call returns an empty paginated response (GET) or {} (writes) so
 * the surrounding UI doesn't crash. Dev/demo convenience, NOT a backend replacement.
 *
 * Everything is seeded from the scenario picked in the dev bar (see
 * scenarios.ts), read once at module load — so switching scenario is a reload,
 * and each scenario keeps its own stores. The default catalogue is at realistic
 * scale (60 contracts, ~108 statements a run) because that is the whole point of
 * the flow; a 3-contract fixture would make every screen look fine.
 *
 * Installed only from seedDevAuth() (gated on import.meta.env.DEV + placeholder
 * keys, or VITE_DEV_AUTH_BYPASS=true), so it is inert in normal production builds.
 */
import { API_BASE_URL } from "@/lib/api/core";
import { registerDevMockRoutes } from "@/lib/dev-mode";
import { SCENARIO_BATCH_ID, activeScenario } from "./scenarios";
import { activeContractCount } from "./testData";

const SCENARIO = activeScenario();

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

/** Deterministic pseudo-random, so every reload seeds the same catalogue. */
function makeRng(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** An ISO date shifted by whole days, for placing a contract term around a period. */
function shiftDays(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  return isoDate(new Date(year, month - 1, day + days));
}

// --- Periods ---------------------------------------------------------------
// Everything is anchored to the last complete half-year -- the cadence a label
// settles on here -- so the prototype always opens on a period that makes
// sense, however long from now it is opened.
function halfRange(year: number, half: number): { start: string; end: string } {
  const firstMonth = (half - 1) * 6;
  return {
    start: isoDate(new Date(year, firstMonth, 1)),
    end: isoDate(new Date(year, firstMonth + 6, 0)),
  };
}

function shiftHalf(year: number, half: number, by: number): { year: number; half: number } {
  const index = year * 2 + (half - 1) + by;
  return { year: Math.floor(index / 2), half: (index % 2) + 1 };
}

const now = new Date();
const currentHalf = now.getMonth() < 6 ? 1 : 2;
/** P0 — the last complete half: the period a label would settle next. */
const P0 = shiftHalf(now.getFullYear(), currentHalf, -1);
/** P1 — the half before it. */
const P1 = shiftHalf(P0.year, P0.half, -1);
/** P2 — two halves back: already generated by an earlier run. */
const P2 = shiftHalf(P1.year, P1.half, -1);

const PERIODS = {
  p0: halfRange(P0.year, P0.half),
  p1: halfRange(P1.year, P1.half),
  p2: halfRange(P2.year, P2.half),
};

// --- Vendors ---------------------------------------------------------------
const DEV_VENDORS = [
  { id: "vendor-spotify", name: "Spotify", default_currency: "USD", supported_import_types: [] },
  { id: "vendor-apple", name: "Apple Music", default_currency: "USD", supported_import_types: [] },
  { id: "vendor-believe", name: "Believe", default_currency: "USD", supported_import_types: [] },
  {
    id: "vendor-youtube",
    name: "YouTube Music",
    default_currency: "USD",
    supported_import_types: [],
  },
];

const VENDOR_IDS = DEV_VENDORS.map((v) => v.id);

interface MockRevenueStatement {
  id: string;
  name: string | null;
  vendor: string;
  period_start: string;
  period_end: string;
  is_settled: boolean;
}

/**
 * Revenue statements the label has received, per scenario. Only the settled ones
 * feed the figures; a single draft one covering the period blocks the whole run.
 */
function buildRevenueStatements(): MockRevenueStatement[] {
  const statements: MockRevenueStatement[] = [];

  // Earlier periods are always fully settled — they are history.
  for (const [key, period] of [
    ["p1", PERIODS.p1],
    ["p2", PERIODS.p2],
  ] as const) {
    for (const vendor of VENDOR_IDS) {
      statements.push({
        id: `rev-${key}-${vendor}`,
        name: null,
        vendor,
        period_start: period.start,
        period_end: period.end,
        is_settled: true,
      });
    }
  }

  const p0 = PERIODS.p0;
  const quarterly = (vendor: string, is_settled: boolean) => ({
    id: `rev-p0-${vendor}`,
    name: null,
    vendor,
    period_start: p0.start,
    period_end: p0.end,
    is_settled,
  });

  switch (SCENARIO.revenue) {
    case "draft":
      statements.push(
        quarterly("vendor-spotify", true),
        quarterly("vendor-apple", true),
        quarterly("vendor-believe", true),
        quarterly("vendor-youtube", false),
      );
      break;
    case "none":
      break;
    default:
      // The default fixture: every vendor reported and settled, so the revenue
      // list is a realistic four rows rather than a single token one.
      statements.push(
        quarterly("vendor-spotify", true),
        quarterly("vendor-apple", true),
        quarterly("vendor-believe", true),
        quarterly("vendor-youtube", true),
      );
  }

  return statements;
}

const REVENUE_STATEMENTS = buildRevenueStatements();

/** Revenue statements overlapping the queried period, whatever its shape. */
function revenueStatementsFor(start: string, end: string): MockRevenueStatement[] {
  if (!start || !end) return [];
  return REVENUE_STATEMENTS.filter((s) => s.period_start <= end && s.period_end >= start);
}

function hasSettledRevenueFor(start: string, end: string): boolean {
  return revenueStatementsFor(start, end).some((s) => s.is_settled);
}

// --- Payees ----------------------------------------------------------------
const FIRST_NAMES = [
  "Adele",
  "Beyoncé",
  "James",
  "Pezet",
  "Sokół",
  "Nina",
  "Miles",
  "Kaya",
  "Oscar",
  "Lena",
  "Theo",
  "Ivy",
  "Marcus",
  "Zofia",
  "Elias",
  "Nora",
  "Felix",
  "Amara",
  "Jonas",
  "Clara",
  "Hugo",
  "Maja",
  "Rafael",
  "Sienna",
  "Otis",
  "Freya",
  "Louis",
  "Alba",
  "Caspar",
  "Wren",
];
const LAST_NAMES = [
  "Adkins",
  "Knowles",
  "Blake",
  "Kowalski",
  "Nowak",
  "Lindqvist",
  "Okafor",
  "Duarte",
  "Fischer",
  "Moreau",
  "Ricci",
  "Novak",
  "Bergström",
  "Silva",
  "Haddad",
  "Ferreira",
  "Zieliński",
  "Kovács",
  "Andersen",
  "Costa",
];

const DEV_PAYEES = Array.from({ length: 300 }, (_, i) => {
  const name = `${FIRST_NAMES[i % FIRST_NAMES.length]} ${LAST_NAMES[(i * 7) % LAST_NAMES.length]}`;
  return {
    id: `payee-${i + 1}`,
    name: i < FIRST_NAMES.length ? name : `${name} ${Math.floor(i / FIRST_NAMES.length) + 1}`,
    legal_name: i % 3 === 0 ? `${name.split(" ")[1]} Music Ltd` : null,
    is_active: true,
    preferred_currency: "USD",
    country: ["US", "GB", "PL", "DE", "SE"][i % 5],
    city: ["New York", "London", "Warsaw", "Berlin", "Stockholm"][i % 5],
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  };
});

function payeeById(id: string) {
  return DEV_PAYEES.find((p) => p.id === id);
}

function payeeName(id: string): string {
  return payeeById(id)?.name ?? id;
}

// --- Contracts -------------------------------------------------------------
// A catalogue with the shapes a run has to cope with: multi-payee contracts,
// contracts with no revenue in the period, ones that can't take part at all
// (draft / no payees), and one whose generation fails.
const RELEASES = [
  "Warsaw Nights",
  "Night Drive",
  "Back in Black",
  "Skyline",
  "Echoes",
  "Paper Boats",
  "Slow Static",
  "Golden Hour",
  "Riverbed",
  "Neon Field",
  "Blue Harbour",
  "Ash & Ember",
  "Winter Tape",
  "Half Light",
  "Wildflower",
  "Cold Signal",
  "Sun Bleached",
  "Late Reply",
  "Marble Sky",
  "Open Water",
];
const DEAL_TYPES = [
  "Recording Agreement",
  "Distribution Deal",
  "Publishing Deal",
  "Licensing Deal",
];
/** Every seeded contract runs from the same date; only the end date varies. */
const CONTRACT_START_DATE = "2024-01-01";
const TRACK_TITLES = [
  "First Light",
  "Undertow",
  "Slow Burn",
  "Faultline",
  "Nightshift",
  "Paper Cut",
  "Kerosene",
  "Glasshouse",
  "Tidal",
  "Afterglow",
];

interface SeedContract {
  id: string;
  name: string;
  status: "active" | "draft" | "ended";
  /** The contract term. `endDate` is set only on an ended contract. */
  startDate: string;
  endDate: string | null;
  payment_threshold: string | null;
  payeeIds: string[];
  shares: number[];
  tracksCount: number;
  allTracksHaveIsrc: boolean;
  firstTrack: string | null;
  /** No revenue in any period — still generates a zero statement. */
  noRevenue: boolean;
  /** No expenses booked in the period, so the pre-flight warning fires. */
  noExpenses: boolean;
  /** Expenses outrun revenue, so the statement value is negative. */
  negative: boolean;
  /** An unpaid statement in the previous period, carried into this one. */
  carriesBalance: boolean;
  /** Generation fails for this contract, to exercise partial failure. */
  fails: boolean;
  /** Tiny earnings, so the balance stays under the payout threshold. */
  tiny: boolean;
}

/**
 * What a catalogue is made of. The three ways a contract can't generate — no
 * payees, no tracks, a track without an ISRC.
 * Status is not one of them: Draft and Ended contracts generate normally; an
 * ended one only drops out once its term ends before the period starts.
 */
interface CatalogueShape {
  count: number;
  hasNoPayees: (i: number) => boolean;
  hasNoTracks: (i: number) => boolean;
  missingIsrc: (i: number) => boolean;
  isDraft: (i: number) => boolean;
  isEnded: (i: number) => boolean;
}

const CATALOGUE_SHAPES: Record<string, CatalogueShape> = {
  typical: {
    count: 60,
    // Spaced by modulo rather than pinned to indices, so the exclusions keep
    // their share of the catalogue when the dev bar resizes it. At 60 that is
    // the same handful it always was.
    hasNoPayees: (i) => i % 29 === 7,
    hasNoTracks: (i) => i % 43 === 19,
    missingIsrc: (i) => i % 47 === 44,
    isDraft: (i) => i % 31 === 12,
    isEnded: (i) => i % 28 === 23,
  },
  large: {
    count: 185,
    hasNoPayees: (i) => i % 41 === 9,
    hasNoTracks: (i) => i % 53 === 12,
    missingIsrc: (i) => i % 47 === 20,
    isDraft: (i) => i % 37 === 5,
    isEnded: (i) => i % 29 === 11,
  },
  many_excluded: {
    count: 56,
    hasNoPayees: (i) => i % 11 === 3,
    hasNoTracks: (i) => i % 11 === 5,
    missingIsrc: (i) => i % 11 === 7,
    isDraft: (i) => i % 13 === 2,
    isEnded: (i) => i % 17 === 4,
  },
  none_eligible: {
    count: 12,
    hasNoPayees: (i) => i % 3 === 0,
    hasNoTracks: (i) => i % 3 === 1,
    missingIsrc: (i) => i % 3 === 2,
    isDraft: (i) => i % 4 === 0,
    isEnded: (i) => i % 4 === 1,
  },
};

/**
 * Where an ended contract's term falls relative to the period a run opens on.
 * Only the first is outside the run: the other two were live for part of the
 * period, earned in it, and still owe a statement.
 */
const ENDED_TERMS = [
  shiftDays(PERIODS.p0.start, -30),
  shiftDays(PERIODS.p0.start, 45),
  shiftDays(PERIODS.p0.end, 20),
];

const DEV_CONTRACTS: SeedContract[] = (() => {
  const base = CATALOGUE_SHAPES[SCENARIO.catalogue] ?? CATALOGUE_SHAPES.typical;
  /* A size picked in the dev bar resizes the catalogue and nothing else. It
     used to clear every exclusion too, so that the number chosen was exactly
     what Eligible held — which quietly emptied the Skipped tab in every
     scenario, including the two whose whole subject is exclusions. The number
     is the size of the catalogue; how it splits is the scenario's business. */
  const sizeOverride = activeContractCount();
  const shape: CatalogueShape =
    sizeOverride === null ? base : { ...base, count: sizeOverride };
  const random = makeRng(20260817);
  const contracts: SeedContract[] = [];
  let payeeCursor = 0;
  let endedCount = 0;

  for (let i = 0; i < shape.count; i += 1) {
    const release = RELEASES[i % RELEASES.length];
    const dealType = DEAL_TYPES[i % DEAL_TYPES.length];
    const suffix = i >= RELEASES.length ? ` ${Math.floor(i / RELEASES.length) + 1}` : "";

    const isDraft = shape.isDraft(i);
    const hasNoPayees = shape.hasNoPayees(i);
    const hasNoTracks = shape.hasNoTracks(i);
    const payeeCount = hasNoPayees ? 0 : 1 + Math.floor(random() * 3);
    const payeeIds = Array.from({ length: payeeCount }, () => {
      const payee = DEV_PAYEES[payeeCursor % DEV_PAYEES.length];
      payeeCursor += 1;
      return payee.id;
    });
    const shares = payeeCount === 1 ? [100] : payeeCount === 2 ? [60, 40] : [50, 30, 20];

    const tracksCount = hasNoTracks ? 0 : 1 + Math.floor(random() * 5);

    // A contract can't be Active without at least one payee.
    const status = isDraft || hasNoPayees ? "draft" : shape.isEnded(i) ? "ended" : "active";
    // Cycled rather than derived from `i`, so even a catalogue with only two
    // ended contracts still shows one on each side of the rule.
    const endDate = status === "ended" ? ENDED_TERMS[endedCount++ % ENDED_TERMS.length] : null;

    contracts.push({
      id: `contract-${i + 1}`,
      name: `${release}${suffix} — ${dealType}`,
      status,
      startDate: CONTRACT_START_DATE,
      endDate,
      payment_threshold: i % 9 === 0 ? null : "50.00",
      payeeIds,
      shares,
      tracksCount,
      allTracksHaveIsrc: !shape.missingIsrc(i),
      firstTrack: tracksCount > 0 ? TRACK_TITLES[i % TRACK_TITLES.length] : null,
      // What a real run is made of, all in the one set rather than split across
      // presets: some contracts unrecouped, some carrying a balance forward,
      // some without revenue for the period, some without expenses booked.
      noRevenue: i % 11 === 3,
      noExpenses: i % 8 === 6,
      negative: i % 7 === 0,
      carriesBalance: i % 3 !== 0,
      fails: i === 26 || (SCENARIO.outcome === "partial_failure" && i % 17 === 2),
      tiny: i % 13 === 5,
    });
  }
  return contracts;
})();

function contractById(id: string): SeedContract | undefined {
  return DEV_CONTRACTS.find((c) => c.id === id);
}

/**
 * Whether a contract can generate at all — the app's existing condition, from
 * computeContractSections(): payees with splits, tracks, and ISRCs on them.
 * Status plays no part.
 */
function canGenerate(contract: SeedContract): boolean {
  return (
    contract.payeeIds.length > 0 &&
    contract.shares.slice(0, contract.payeeIds.length).every((share) => share > 0) &&
    contract.tracksCount > 0 &&
    contract.allTracksHaveIsrc
  );
}

function eligibleContracts(): SeedContract[] {
  return DEV_CONTRACTS.filter(canGenerate);
}

/** The Contract shape the app's contracts endpoints return. */
function toApiContract(contract: SeedContract): object {
  return {
    id: contract.id,
    name: contract.name,
    status: contract.status,
    contract_type: "exclusive_license",
    currency: "USD",
    start_date: contract.startDate,
    end_date: contract.endDate,
    territory: "Worldwide",
    statement_period: "quarterly",
    starting_balance: null,
    payment_threshold: contract.payment_threshold,
    label: "dev-label",
    label_name: "Dev Label",
    party: "Dev Label",
    releases_count: 1,
    tracks_count: contract.tracksCount,
    all_tracks_have_isrc: contract.allTracksHaveIsrc,
    first_sound_recording_title: contract.firstTrack,
    can_delete: false,
    can_activate: true,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    payees: toApiPayees(contract),
    related_recordings: [],
    related_releases: [],
  };
}

function toApiPayees(contract: SeedContract) {
  return contract.payeeIds.map((payeeId, index) => ({
    matched: true,
    payee: { id: payeeId, name: payeeName(payeeId) },
    share_percentage: contract.shares[index],
    opening_balance: null,
  }));
}

// --- Money -----------------------------------------------------------------
/** Revenue attributed to a contract in a period — stable per contract+period. */
function contractRevenue(contract: SeedContract, start: string, end: string): number {
  if (contract.noRevenue) return 0;
  // No settled distributor revenue landed for this period.
  if (!hasSettledRevenueFor(start, end)) return 0;
  const seed = Number(start.replace(/-/g, "")) + Number(contract.id.split("-")[1]) * 977;
  const random = makeRng(seed);
  if (contract.tiny) return Math.round(random() * 60 * 100) / 100;
  if (contract.negative) return Math.round(random() * 400 * 100) / 100;
  return Math.round((400 + random() * 9000) * 100) / 100;
}

/** Expenses booked against the contract in the period. */
function contractExpenses(contract: SeedContract, start: string, end: string): number {
  if (contract.noExpenses) return 0;
  if (contract.negative) return contractRevenue(contract, start, end) + 250;
  const seed = Number(start.replace(/-/g, "")) + Number(contract.id.split("-")[1]) * 31;
  const random = makeRng(seed);
  return contract.noRevenue || contract.tiny ? 0 : Math.round(random() * 300 * 100) / 100;
}

function money(value: number): string {
  return value.toFixed(2);
}

// --- Royalty statement store ----------------------------------------------
// Keyed per scenario, so switching scenario never serves another one's data.
const ROYALTY_STORE_KEY = `dev-mock-brs-royalty-${SCENARIO.id}`;
const BATCH_STORE_KEY = `dev-mock-brs-batches-${SCENARIO.id}`;
const LABEL_STORE_KEY = "dev-mock-brs-label-v1";

const DEV_INVOICE_FILE =
  "data:text/csv;charset=utf-8,Description%2CAmount%0ANet%20royalties%20for%20period%2C4210.55";

interface MockStatement {
  id: string;
  statement_no: string;
  payee: string;
  contract: string;
  period_start: string;
  period_end: string;
  statement_value: string;
  previous_balance: string;
  statement_balance: string;
  payment_threshold: string;
  meets_payment_threshold: boolean;
  currency: string;
  status: string;
  batch: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface MockBatch {
  id: string;
  period_start: string;
  period_end: string;
  created_at: string;
  created_by: string | null;
  contracts_count: number;
  total: number;
  /** Statement ids in generation order — progress walks this list. */
  statement_ids: string[];
  failed_ids: string[];
  /** Epoch ms the run started; progress is derived from elapsed time. */
  started_at: number;
  /** Set once the run has finished, so a completed batch stops moving. */
  finished: boolean;
  /** Scenario runs freeze progress here instead of letting the clock finish it. */
  frozen_done?: number;
  /** Stopped part-way by the user: finished, but not complete. */
  cancelled?: boolean;
  /** Seeded by the scenario rather than started by the user in this session. */
  seeded?: boolean;
}

let statementSeq = 0;
function nextStatementNo(period: string): string {
  statementSeq += 1;
  return `RS-${period.slice(0, 4)}-${String(1000 + statementSeq).padStart(4, "0")}`;
}

const HISTORY_BATCH_ID = "batch-seeded-p2";

/**
 * The money on one payee's statement: this period's share, plus anything still
 * unpaid from earlier periods, against the contract's payout threshold.
 */
function buildAmounts(
  contract: SeedContract,
  payeeId: string,
  payeeIndex: number,
  periodStart: string,
  periodEnd: string,
  existing: MockStatement[],
) {
  const share = (contract.shares[payeeIndex] ?? 100) / 100;
  const net =
    contractRevenue(contract, periodStart, periodEnd) -
    contractExpenses(contract, periodStart, periodEnd);
  const statementValue = net * share;
  const previous = existing
    .filter(
      (s) =>
        s.payee === payeeId &&
        s.contract === contract.id &&
        s.status !== "paid" &&
        s.period_end < periodStart,
    )
    .reduce((sum, s) => Math.max(sum, Number(s.statement_balance) || 0), 0);
  const balance = previous + statementValue;
  const threshold = contract.payment_threshold ?? "";
  return {
    statement_value: money(statementValue),
    previous_balance: money(previous),
    statement_balance: money(balance),
    payment_threshold: threshold,
    meets_payment_threshold: !threshold || balance >= Number(threshold),
  };
}

function makeStatement(
  contract: SeedContract,
  payeeId: string,
  payeeIndex: number,
  period: { start: string; end: string },
  existing: MockStatement[],
  overrides: Partial<MockStatement>,
): MockStatement {
  return {
    id: `roy-${period.start}-${contract.id}-${payeeId}`,
    statement_no: nextStatementNo(period.start),
    payee: payeeId,
    contract: contract.id,
    period_start: period.start,
    period_end: period.end,
    ...buildAmounts(contract, payeeId, payeeIndex, period.start, period.end, existing),
    currency: "USD",
    status: "draft",
    batch: null,
    created_by: "dev@local.test",
    created_at: `${period.end}T18:00:00Z`,
    updated_at: `${period.end}T18:00:00Z`,
    ...overrides,
  };
}

/**
 * Seeds the statements this scenario needs: history for an earlier period, any
 * carried-forward balances, and the run an outcome scenario opens on.
 */
function seedStatements(): { statements: MockStatement[]; batches: MockBatch[] } {
  const statements: MockStatement[] = [];
  const batches: MockBatch[] = [];
  const eligible = eligibleContracts();

  // Two periods back: generated by an earlier run, so the statements list opens
  // with history behind it.
  for (const contract of eligible) {
    contract.payeeIds.forEach((payeeId, index) => {
      statements.push(
        makeStatement(contract, payeeId, index, PERIODS.p2, statements, {
          batch: HISTORY_BATCH_ID,
          status: index === 0 ? "sent" : index === 1 ? "paid" : "draft",
        }),
      );
    });
  }
  if (statements.length > 0) {
    batches.push({
      id: HISTORY_BATCH_ID,
      period_start: PERIODS.p2.start,
      period_end: PERIODS.p2.end,
      created_at: `${PERIODS.p2.end}T18:00:00Z`,
      created_by: "dev@local.test",
      contracts_count: eligible.length,
      total: statements.length,
      statement_ids: [],
      failed_ids: [],
      started_at: 0,
      finished: true,
      seeded: true,
    });
  }

  // The previous quarter, left unpaid on a good share of the catalogue, so the
  // run carries balances forward the way a real one does.
  for (const contract of eligible.filter((c) => c.carriesBalance)) {
    contract.payeeIds.forEach((payeeId, index) => {
      statements.push(
        makeStatement(contract, payeeId, index, PERIODS.p1, statements, { status: "sent" }),
      );
    });
  }

  /* The period the panel opens on, with its statements already generated — the
     one scenario where that collision exists. Everywhere else the default period
     is untouched, so the run is never asked to settle a period twice. */
  if (SCENARIO.periodHasStatements) {
    for (const contract of eligible) {
      contract.payeeIds.forEach((payeeId, index) => {
        statements.push(
          makeStatement(contract, payeeId, index, PERIODS.p0, statements, { status: "sent" }),
        );
      });
    }
  }

  // An outcome scenario opens on a run that has already been started.
  if (SCENARIO.outcome) {
    const runContracts = eligible;
    const created: MockStatement[] = [];
    for (const contract of runContracts) {
      contract.payeeIds.forEach((payeeId, index) => {
        created.push(
          makeStatement(contract, payeeId, index, PERIODS.p0, [...statements, ...created], {
            batch: SCENARIO_BATCH_ID,
            status: "draft",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }),
        );
      });
    }

    const failedIds =
      SCENARIO.outcome === "partial_failure"
        ? created.filter((s) => contractById(s.contract)?.fails).map((s) => s.id)
        : [];
    // Held part-way through, so the progress state can be reviewed at leisure
    // rather than for the few seconds a real run would take.
    const frozenDone =
      SCENARIO.outcome === "generating" ? Math.floor(created.length * 0.4) : undefined;

    for (const [index, statement] of created.entries()) {
      if (failedIds.includes(statement.id)) statement.status = "failed";
      else if (frozenDone !== undefined && index >= frozenDone) statement.status = "pending";
    }

    statements.push(...created);
    batches.push({
      id: SCENARIO_BATCH_ID,
      period_start: PERIODS.p0.start,
      period_end: PERIODS.p0.end,
      created_at: new Date().toISOString(),
      created_by: "dev@local.test",
      contracts_count: runContracts.length,
      total: created.length,
      statement_ids: created.map((s) => s.id),
      failed_ids: failedIds,
      started_at: Date.now(),
      finished: frozenDone === undefined,
      frozen_done: frozenDone,
      seeded: true,
    });
  }

  return { statements, batches };
}

function loadStatements(): MockStatement[] {
  const raw = localStorage.getItem(ROYALTY_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  const { statements, batches } = seedStatements();
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(statements));
  localStorage.setItem(BATCH_STORE_KEY, JSON.stringify(batches));
  return statements;
}

function saveStatements(list: MockStatement[]): void {
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(list));
}

function loadBatches(): MockBatch[] {
  const raw = localStorage.getItem(BATCH_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  // Seeding writes both stores together.
  loadStatements();
  try {
    return JSON.parse(localStorage.getItem(BATCH_STORE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveBatches(list: MockBatch[]): void {
  localStorage.setItem(BATCH_STORE_KEY, JSON.stringify(list));
}

// --- API shapes ------------------------------------------------------------
function payeeDetails(payeeId: string) {
  const payee = payeeById(payeeId);
  return {
    id: payeeId,
    name: payee?.name ?? payeeId,
    legal_name: payee?.legal_name ?? null,
    preferred_currency: "USD",
    country: payee?.country ?? "US",
    contact_email: `${payeeId}@example.com`,
    is_active: true,
  };
}

function contractDetails(contractId: string) {
  const contract = contractById(contractId);
  return {
    id: contractId,
    name: contract?.name ?? "Contract",
    contract_type: "exclusive_license",
    status: contract?.status ?? "active",
    start_date: contract?.startDate ?? CONTRACT_START_DATE,
    end_date: contract?.endDate ?? null,
    currency: "USD",
  };
}

function toApiStatement(statement: MockStatement): object {
  const isGenerating = statement.status === "pending" || statement.status === "generating";
  return {
    ...statement,
    payee_details: payeeDetails(statement.payee),
    contract_details: contractDetails(statement.contract),
    file: isGenerating ? null : DEV_INVOICE_FILE,
    file_url: isGenerating ? null : DEV_INVOICE_FILE,
  };
}

// --- Run preview -----------------------------------------------------------
/**
 * Whether the contract term still touches the period being settled.
 *
 * An ended contract that was live for any part of the period earned in it, so
 * it belongs in the run exactly like an active one — a contract ending on 12
 * February still owes a statement for Q1. Only a term that closed before the
 * period started is outside the run.
 */
function endedBeforePeriod(contract: SeedContract, start: string): boolean {
  return contract.endDate !== null && contract.endDate < start;
}

/**
 * The contracts a period covers, derived rather than picked. Everything with at
 * least one payee that was live in the period is included — including contracts
 * with no revenue, which still get a zero statement — and only structural
 * problems, an existing statement, or a term that closed before the period
 * exclude one.
 */
/**
 * The derived contracts, each still carrying the statements it would produce.
 *
 * Internal: the API shape below sends a count instead, because the rows
 * themselves are served a page at a time from run/preview/statements.
 */
function runPreviewContracts(start: string, end: string) {
  const statements = loadStatements();

  const contracts = DEV_CONTRACTS.map((contract) => {
    let exclusionReason: string | null = null;
    if (!canGenerate(contract)) exclusionReason = "incomplete";
    else if (endedBeforePeriod(contract, start)) exclusionReason = "contract_ended";

    const previews = contract.payeeIds.map((payeeId, index) => {
      const amounts = buildAmounts(contract, payeeId, index, start, end, statements);
      return {
        contract: contract.id,
        contract_name: contract.name,
        payee: payeeId,
        payee_name: payeeName(payeeId),
        total_revenue: money(contractRevenue(contract, start, end)),
        ...amounts,
        currency: "USD",
      };
    });

    return {
      id: contract.id,
      name: contract.name,
      currency: "USD",
      status: contract.status,
      start_date: contract.startDate,
      end_date: contract.endDate,
      tracks_count: contract.tracksCount,
      all_tracks_have_isrc: contract.allTracksHaveIsrc,
      first_sound_recording_title: contract.firstTrack,
      payees: toApiPayees(contract),
      included: exclusionReason === null,
      exclusion_reason: exclusionReason,
      has_revenue: contractRevenue(contract, start, end) > 0,
      has_expenses: contractExpenses(contract, start, end) > 0,
      statements: previews,
    };
  });

  return contracts;
}

function runPreview(start: string, end: string): object {
  // The statement rows come from run/preview/statements; a contract carries
  // only how many of them it accounts for, so the counts on screen stay exact
  // without the whole catalogue's statements travelling with every request.
  const contracts = runPreviewContracts(start, end).map(({ statements, ...contract }) => ({
    ...contract,
    statement_count: statements.length,
  }));

  return {
    period_start: start,
    period_end: end,
    contracts,
    revenue_statements: revenueStatementsFor(start, end),
    // Asked of the period, not of each contract: one statement anywhere in it
    // is enough for the run to be refused.
    period_has_statements: loadStatements().some(
      (s) => s.period_start === start && s.period_end === end,
    ),
  };
}

/**
 * One page of the statements a run would produce.
 *
 * The rule the whole preview hangs on lives here rather than in the panel: the
 * order — anything owing a balance back first — has to be decided over the
 * whole run, because a page is a window onto it. Sorting a page client-side
 * would only sort that window, and the balances owed would scatter across pages.
 */
function runPreviewStatements(
  start: string,
  end: string,
  selection: { optedOut: string[]; optedIn: string[] },
  page: number,
  pageSize: number,
): object {
  const contracts = runPreviewContracts(start, end);

  // The same selection the form applies, applied where the list is built.
  const rejoined = contracts.filter(
    (c) => c.exclusion_reason === "contract_ended" && selection.optedIn.includes(c.id),
  );
  const running = [...contracts.filter((c) => c.included), ...rejoined].filter(
    (c) => !selection.optedOut.includes(c.id),
  );

  const all = running.flatMap((c) => c.statements);
  const owed = all.filter((s) => Number(s.statement_balance) < 0);
  const rest = all.filter((s) => !(Number(s.statement_balance) < 0));
  const ordered = [...owed, ...rest];

  const from = (page - 1) * pageSize;
  const results = ordered.slice(from, from + pageSize);
  return {
    results,
    count: ordered.length,
    next: from + pageSize < ordered.length ? String(page + 1) : null,
    previous: page > 1 ? String(page - 1) : null,
  };
}

// --- Run -------------------------------------------------------------------
/** How long one statement takes to generate, so progress is watchable. */
const GENERATION_MS_PER_STATEMENT = 80;

function startRun(body: any): object {
  const start: string = body?.period_start;
  const end: string = body?.period_end;
  const contractIds: string[] = Array.isArray(body?.contracts) ? body.contracts : [];
  const statements = loadStatements();

  const batchId = `batch-${crypto.randomUUID()}`;
  const created: MockStatement[] = [];
  for (const contractId of contractIds) {
    const contract = contractById(contractId);
    if (!contract) continue;
    for (const [index, payeeId] of contract.payeeIds.entries()) {
      const amounts = buildAmounts(contract, payeeId, index, start, end, statements);
      created.push({
        id: crypto.randomUUID(),
        statement_no: nextStatementNo(start),
        payee: payeeId,
        contract: contractId,
        period_start: start,
        period_end: end,
        ...amounts,
        currency: "USD",
        status: "pending",
        batch: batchId,
        created_by: "dev@local.test",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  }

  const failedIds = created.filter((s) => contractById(s.contract)?.fails).map((s) => s.id);

  const batch: MockBatch = {
    id: batchId,
    period_start: start,
    period_end: end,
    created_at: new Date().toISOString(),
    created_by: "dev@local.test",
    contracts_count: contractIds.length,
    total: created.length,
    statement_ids: created.map((s) => s.id),
    failed_ids: failedIds,
    started_at: Date.now(),
    finished: false,
  };

  saveStatements([...created, ...statements]);
  saveBatches([batch, ...loadBatches()]);
  return toApiBatch(batch);
}

/**
 * Walks the batch forward by elapsed time: each statement flips from `pending`
 * to `draft` (or `failed`) in turn, so the UI sees a real background job
 * without the mock holding timers. A frozen batch stays where it is.
 */
function advanceBatch(batch: MockBatch): MockBatch {
  if (batch.finished || batch.statement_ids.length === 0) return batch;
  if (typeof batch.frozen_done === "number") return batch;

  const done = Math.floor((Date.now() - batch.started_at) / GENERATION_MS_PER_STATEMENT);
  const statements = loadStatements();
  let changed = false;

  batch.statement_ids.forEach((id, index) => {
    const statement = statements.find((s) => s.id === id);
    if (statement?.status !== "pending") return;
    if (index >= done) return;
    statement.status = batch.failed_ids.includes(id) ? "failed" : "draft";
    statement.updated_at = new Date().toISOString();
    changed = true;
  });

  if (changed) saveStatements(statements);

  if (done >= batch.statement_ids.length) {
    batch.finished = true;
    const batches = loadBatches().map((b) => (b.id === batch.id ? batch : b));
    saveBatches(batches);
  }
  return batch;
}

function toApiBatch(batch: MockBatch): object {
  const statements = loadStatements();
  const own = batch.statement_ids.length
    ? statements.filter((s) => batch.statement_ids.includes(s.id))
    : statements.filter((s) => s.batch === batch.id);

  const generated = own.filter((s) => s.status !== "pending" && s.status !== "failed").length;
  const failed = own.filter((s) => s.status === "failed").length;
  const stillPending = own.some((s) => s.status === "pending");
  const status = batch.cancelled
    ? "cancelled"
    : stillPending
      ? "generating"
      : failed > 0
        ? "partial"
        : "completed";

  return {
    id: batch.id,
    period_start: batch.period_start,
    period_end: batch.period_end,
    created_at: batch.created_at,
    created_by: batch.created_by,
    status,
    contracts_count: batch.contracts_count,
    total: batch.total || own.length,
    generated,
    failed,
    failures: own
      .filter((s) => s.status === "failed")
      .map((s) => ({
        contract_name: contractById(s.contract)?.name ?? "Contract",
        payee_name: payeeName(s.payee),
      })),
    sent: own.filter((s) => s.status === "sent" || s.status === "paid").length,
  };
}

function retryBatch(batch: MockBatch): object {
  const statements = loadStatements();
  for (const statement of statements) {
    if (statement.status === "failed" && statement.batch === batch.id) {
      statement.status = "draft";
      statement.updated_at = new Date().toISOString();
    }
  }
  saveStatements(statements);
  const retried: MockBatch = { ...batch, failed_ids: [] };
  saveBatches(loadBatches().map((b) => (b.id === batch.id ? retried : b)));
  return toApiBatch(retried);
}

/** Undo drops the run's statements, except any already sent or paid. */
/**
 * Stops a run part-way. Whatever the clock has already generated is kept — those
 * statements exist and a payee could already have been told about them — and the
 * queued ones are dropped, since they were never made.
 */
function cancelBatch(batch: MockBatch): object {
  const advanced = advanceBatch(batch);
  const kept = loadStatements().filter((s) => s.batch !== batch.id || s.status !== "pending");
  saveStatements(kept);

  const keptIds = new Set(kept.map((s) => s.id));
  const stopped: MockBatch = {
    ...advanced,
    statement_ids: advanced.statement_ids.filter((id) => keptIds.has(id)),
    total: advanced.statement_ids.filter((id) => keptIds.has(id)).length,
    contracts_count: new Set(kept.filter((s) => s.batch === batch.id).map((s) => s.contract)).size,
    finished: true,
    frozen_done: undefined,
    cancelled: true,
  };
  saveBatches(loadBatches().map((b) => (b.id === batch.id ? stopped : b)));
  return toApiBatch(stopped);
}

function undoBatch(batch: MockBatch): void {
  const statements = loadStatements().filter(
    (s) => s.batch !== batch.id || s.status === "sent" || s.status === "paid",
  );
  saveStatements(statements);
  saveBatches(loadBatches().filter((b) => b.id !== batch.id));
}

// --- Statement documents ---------------------------------------------------
function statementHtml(statement: MockStatement): string {
  const contract = contractById(statement.contract);
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:32px;max-width:720px;margin:0 auto}
    h1{font-size:20px;margin:0 0 4px} .muted{color:#666;font-size:13px}
    table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
    th,td{text-align:left;padding:8px 0;border-bottom:1px solid #eee} td.r,th.r{text-align:right}
    .total{font-weight:600;font-size:16px}
  </style></head><body>
    <h1>Royalty statement ${statement.statement_no}</h1>
    <div class="muted">${payeeName(statement.payee)} · ${contract?.name ?? ""}</div>
    <div class="muted">Period: ${statement.period_start} – ${statement.period_end}</div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Statement value</td><td class="r">${statement.statement_value} ${statement.currency}</td></tr>
      <tr><td>Previous balance</td><td class="r">${statement.previous_balance} ${statement.currency}</td></tr>
      <tr><td class="total">Balance</td><td class="r total">${statement.statement_balance} ${statement.currency}</td></tr>
    </table>
  </body></html>`;
}

/** Per-payee preview of a statement that hasn't been generated yet. */
function previewHtml(query: URLSearchParams): string {
  const contract = contractById(query.get("contract_id") ?? "");
  const payeeId = query.get("payee_id") ?? "";
  const start = query.get("period_start") ?? "";
  const end = query.get("period_end") ?? "";
  if (!contract) return "<!doctype html><html><body></body></html>";

  const index = contract.payeeIds.indexOf(payeeId);
  const amounts = buildAmounts(contract, payeeId, Math.max(index, 0), start, end, loadStatements());
  const revenue = contractRevenue(contract, start, end);
  const expenses = contractExpenses(contract, start, end);
  const share = contract.shares[Math.max(index, 0)] ?? 100;

  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:32px}
    h1{font-size:18px;margin:0 0 4px} .muted{color:#666;font-size:13px}
    table{width:100%;border-collapse:collapse;margin-top:20px;font-size:13px}
    th,td{text-align:left;padding:8px 0;border-bottom:1px solid #eee} td.r,th.r{text-align:right}
    .total{font-weight:600;font-size:15px}
  </style></head><body>
    <h1>${payeeName(payeeId)}</h1>
    <div class="muted">${contract.name}</div>
    <div class="muted">Period: ${start} – ${end} · Payee share (%): ${share}</div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Total revenue</td><td class="r">${money(revenue)} USD</td></tr>
      <tr><td>Expenses</td><td class="r">-${money(expenses)} USD</td></tr>
      <tr><td>Statement value</td><td class="r">${amounts.statement_value} USD</td></tr>
      <tr><td>Previous balance</td><td class="r">${amounts.previous_balance} USD</td></tr>
      <tr><td class="total">Balance</td><td class="r total">${amounts.statement_balance} USD</td></tr>
    </table>
  </body></html>`;
}

function archiveUrl(statements: MockStatement[]): string {
  const lines = [
    "Statement,Payee,Contract,Period,Balance",
    ...statements.map((s) =>
      [
        s.statement_no,
        payeeName(s.payee),
        contractById(s.contract)?.name ?? "",
        `${s.period_start} - ${s.period_end}`,
        s.statement_balance,
      ].join(","),
    ),
  ];
  return `data:text/csv;charset=utf-8,${encodeURIComponent(lines.join("\n"))}`;
}

function batchArchiveUrl(batch: MockBatch): string {
  return archiveUrl(loadStatements().filter((s) => s.batch === batch.id));
}

// --- Label (Settings view) -------------------------------------------------
function loadLabel(): any {
  const raw = localStorage.getItem(LABEL_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  const label = {
    id: "dev-label",
    name: "Dev Label",
    currency: "USD",
    payment_threshold: "50.00",
    can_update_currency: true,
    country: "US",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
  };
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
  return label;
}

function saveLabel(label: any): void {
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
}

// --- Reset (prototype affordance) -----------------------------------------
/** Wipes this scenario's data so the next load re-seeds it from scratch. */
export function resetPrototype(): void {
  for (const key of [ROYALTY_STORE_KEY, BATCH_STORE_KEY, LABEL_STORE_KEY]) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem("organization-selection");
}

function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // --- Label (Settings) ----------------------------------------------------
  if (trimmed === "/accounts/labels" && method === "GET") {
    return jsonResponse(paginated([loadLabel()]));
  }
  if (trimmed.startsWith("/accounts/labels/")) {
    const id = trimmed.slice("/accounts/labels/".length);
    const label = loadLabel();
    if (id !== label.id) return jsonResponse({ detail: "Not found." }, 404);
    if (method === "GET") return jsonResponse(label);
    if (method === "PATCH" || method === "PUT") {
      const updated = { ...label, ...body, updated_at: new Date().toISOString() };
      saveLabel(updated);
      return jsonResponse(updated);
    }
  }

  // --- Vendors -------------------------------------------------------------
  if (trimmed === "/imports/vendors" && method === "GET") {
    return jsonResponse(DEV_VENDORS);
  }

  // --- Payees (match BEFORE /contracts/:id) --------------------------------
  if (trimmed === "/contracts/payees" && method === "GET") {
    return jsonResponse(paginated(DEV_PAYEES));
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = payeeById(id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // --- Royalty run ---------------------------------------------------------
  if (trimmed === "/revenue/royalty-statements/run/default-period" && method === "GET") {
    // The cadence the label last ran on wins; otherwise the period the most
    // recent settled revenue covers.
    const batches = loadBatches();
    // Only a run this session actually started counts as history for the
    // default — a scenario-seeded one is scaffolding, not the label's cadence.
    const lastRun = batches
      .filter((b) => b.started_at > 0 && !b.seeded)
      .sort((a, b) => b.started_at - a.started_at)[0];
    if (lastRun) {
      // Same cadence, one period on from the last run.
      const next = shiftHalf(
        Number(lastRun.period_start.slice(0, 4)),
        Number(lastRun.period_start.slice(5, 7)) <= 6 ? 1 : 2,
        1,
      );
      return jsonResponse({ ...halfRange(next.year, next.half), source: "last_run" });
    }
    return jsonResponse({
      period_start: PERIODS.p0.start,
      period_end: PERIODS.p0.end,
      source: "settled_revenue",
    });
  }
  if (trimmed === "/revenue/royalty-statements/run/preview" && method === "GET") {
    const start = query.get("period_start") ?? "";
    const end = query.get("period_end") ?? "";
    return jsonResponse(runPreview(start, end));
  }
  if (trimmed === "/revenue/royalty-statements/run/preview/statements" && method === "GET") {
    const ids = (name: string) => (query.get(name) ?? "").split(",").filter(Boolean);
    return jsonResponse(
      runPreviewStatements(
        query.get("period_start") ?? "",
        query.get("period_end") ?? "",
        { optedOut: ids("opted_out"), optedIn: ids("opted_in") },
        Number.parseInt(query.get("page") ?? "1", 10) || 1,
        Number.parseInt(query.get("page_size") ?? "50", 10) || 50,
      ),
    );
  }
  if (trimmed === "/revenue/royalty-statements/run" && method === "POST") {
    return jsonResponse(startRun(body), 201);
  }

  // --- Batches -------------------------------------------------------------
  if (trimmed.startsWith("/revenue/royalty-statements/batches/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/batches/".length);
    const [id, sub] = rest.split("/");
    const batch = loadBatches().find((b) => b.id === id);
    if (!batch) return jsonResponse({ detail: "Not found." }, 404);

    if (!sub && method === "GET") return jsonResponse(toApiBatch(advanceBatch(batch)));
    if (sub === "retry" && method === "POST") return jsonResponse(retryBatch(batch));
    if (sub === "cancel" && method === "POST") return jsonResponse(cancelBatch(batch));
    if (sub === "archive" && method === "GET") {
      return jsonResponse({ url: batchArchiveUrl(batch) });
    }
    if (!sub && method === "DELETE") {
      undoBatch(batch);
      return jsonResponse(null, 204);
    }
  }

  // --- Royalty statements --------------------------------------------------
  if (trimmed === "/revenue/royalty-statements/archive" && method === "POST") {
    const ids: string[] = Array.isArray(body?.statements) ? body.statements : [];
    const picked = loadStatements().filter((s) => ids.includes(s.id));
    return jsonResponse({ url: archiveUrl(picked) });
  }
  if (trimmed === "/revenue/royalty-statements/preview/html" && method === "GET") {
    return jsonResponse({ html: previewHtml(query) });
  }
  if (trimmed === "/revenue/royalty-statements" && method === "GET") {
    // Keep any in-flight run moving even when only the list is polling.
    for (const batch of loadBatches()) advanceBatch(batch);

    let list = loadStatements();
    const q = (query.get("q") || query.get("search") || "").toLowerCase();
    if (q) {
      list = list.filter((s) =>
        `${s.statement_no} ${payeeName(s.payee)} ${contractById(s.contract)?.name ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    const batchParam = query.get("batch");
    if (batchParam) list = list.filter((s) => s.batch === batchParam);
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((s) => allowed.has(s.status));
    }
    const payeesParam = query.get("payees");
    if (payeesParam) {
      const allowed = new Set(payeesParam.split(","));
      list = list.filter((s) => allowed.has(s.payee));
    }
    const after = query.get("period_start_after");
    const before = query.get("period_end_before");
    if (after) list = list.filter((s) => s.period_start >= after);
    if (before) list = list.filter((s) => s.period_end <= before);

    const ordering = query.get("ordering");
    if (ordering) {
      const desc = ordering.startsWith("-");
      const field = desc ? ordering.slice(1) : ordering;
      list = [...list].sort((a, b) => {
        const cmp = String((a as any)[field] ?? "").localeCompare(
          String((b as any)[field] ?? ""),
          undefined,
          { numeric: true },
        );
        return desc ? -cmp : cmp;
      });
    } else {
      list = [...list].sort((a, b) => b.created_at.localeCompare(a.created_at));
    }

    const page = Number(query.get("page") || 1);
    const pageSize = Number(query.get("page_size") || 25);
    const start = (page - 1) * pageSize;
    const pageItems = list.slice(start, start + pageSize).map(toApiStatement);
    return jsonResponse({
      results: pageItems,
      count: list.length,
      next: start + pageSize < list.length ? "next" : null,
      previous: page > 1 ? "previous" : null,
    });
  }
  if (trimmed.startsWith("/revenue/royalty-statements/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/".length);
    const [id, sub] = rest.split("/");
    const list = loadStatements();
    const index = list.findIndex((s) => s.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found." }, 404);

    if (sub === "html" && method === "GET") {
      return jsonResponse({ html: statementHtml(list[index]) });
    }
    if (sub === "mark-sent" && method === "POST") {
      list[index] = { ...list[index], status: "sent", updated_at: new Date().toISOString() };
      saveStatements(list);
      return jsonResponse(toApiStatement(list[index]));
    }
    if (sub === "mark-paid" && method === "POST") {
      list[index] = { ...list[index], status: "paid", updated_at: new Date().toISOString() };
      saveStatements(list);
      return jsonResponse(toApiStatement(list[index]));
    }
    if (sub === "regenerate" && method === "POST") {
      // Rebuilt from current data.
      const statement = list[index];
      const contract = contractById(statement.contract);
      const payeeIndex = contract?.payeeIds.indexOf(statement.payee) ?? 0;
      const amounts = contract
        ? buildAmounts(
            contract,
            statement.payee,
            Math.max(payeeIndex, 0),
            statement.period_start,
            statement.period_end,
            list.filter((s) => s.id !== statement.id),
          )
        : {};
      list[index] = {
        ...statement,
        ...amounts,
        status: "draft",
        updated_at: new Date().toISOString(),
      };
      saveStatements(list);
      return jsonResponse(toApiStatement(list[index]));
    }
    if (!sub) {
      if (method === "GET") return jsonResponse(toApiStatement(list[index]));
      if (method === "DELETE") {
        list.splice(index, 1);
        saveStatements(list);
        return jsonResponse(null, 204);
      }
    }
  }

  // --- Contracts -----------------------------------------------------------
  if (trimmed === "/contracts" && method === "GET") {
    let list = DEV_CONTRACTS.map(toApiContract) as any[];
    const q = query.get("q")?.toLowerCase();
    if (q) list = list.filter((c) => String(c.name).toLowerCase().includes(q));
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((c) => allowed.has(c.status));
    }
    return jsonResponse(paginated(list));
  }
  if (trimmed.startsWith("/contracts/") && method === "GET") {
    const contract = contractById(trimmed.slice("/contracts/".length));
    return contract
      ? jsonResponse(toApiContract(contract))
      : jsonResponse({ detail: "Not found." }, 404);
  }

  // Unhandled API route: keep the UI alive with a benign empty payload.
  console.warn(`[brs-mock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

/**
 * Hand the routes above to dev-mode's single fetch patch instead of installing
 * another one here. dev-mode only consults handlers for the app's own API base;
 * returning null falls through to its empty default.
 */
registerDevMockRoutes((request) => {
  if (!request.url.startsWith(API_BASE_URL)) return null;

  const [path, queryString = ""] = request.url.slice(API_BASE_URL.length).split("?");
  const query = new URLSearchParams(queryString);

  let body: any;
  const rawBody = request.body;
  if (typeof rawBody === "string") {
    try {
      body = JSON.parse(rawBody);
    } catch {
      body = undefined;
    }
  } else if (rawBody instanceof FormData) {
    body = {};
    rawBody.forEach((value, key) => {
      body[key] = value instanceof File ? value.name : value;
    });
  }

  return route(request.method, path, body, query) ?? null;
});
